<template>
  <div class="row">
    <div class="icon-wrap" :class="colorClass">
      <img :src="getIconPath(type)" :alt="type" class="icon" />
    </div>

    <div class="info">
      <div class="top">
        <strong class="time">{{ formattedTime }}</strong>
        <span class="type" :class="typeClass">{{ type }}</span>
      </div>
      <div class="bottom">
        <div class="dots">
          <span
            v-for="dot in displayDots"
            :key="dot.n"
            class="dot"
            :class="{ on: dot.isOn }"
          ></span>
        </div>
        <span class="count">{{ displayBooked }} / {{ displayCapacity }}</span>
      </div>
    </div>

    <div class="right">
      <span v-if="finalStatus === 'done'" class="chip chip--done"
        >예약 완료</span
      >
      <span v-else-if="finalStatus === 'close'" class="chip chip--close"
        >마감</span
      >
      <span v-else-if="finalStatus === 'warn'" class="chip chip--warn"
        >마감 임박</span
      >
      <UiButton
        v-if="finalStatus === 'open' || finalStatus === 'warn'"
        class="book-btn"
        @click="$emit('book')"
        >예약하기</UiButton
      >
    </div>
  </div>
</template>

<script>
import UiButton from "@/components/UiButton.vue";

export default {
  name: "ScheduleRow",
  components: { UiButton },
  props: {
    time: { type: String, required: true },
    type: { type: String, required: true },
    booked: { type: Number, required: true },
    capacity: { type: Number, required: true },
    status: { type: String, default: "" },
    mine: { type: Boolean, default: false },
  },
  computed: {
    formattedTime() {
      // "10:00:00" -> "10:00" 형식으로 변환
      if (this.time && this.time.includes(":")) {
        const parts = this.time.split(":");
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
      }
      return this.time;
    },
    typeClass() {
      return this.type === "Hybrid"
        ? "hyb"
        : this.type === "Core"
        ? "core"
        : "car";
    },
    colorClass() {
      return this.typeClass;
    },
    capacityDots() {
      // 지난 클래스는 항상 12개, 현재/미래 클래스는 capacity 사용
      const targetCapacity =
        this.status === "done" || this.status === "close"
          ? 12
          : this.capacity || 12;
      return Math.min(targetCapacity, 12);
    },
    displayCapacity() {
      // 지난 클래스(status가 'done'이거나 'close')는 항상 12로 표시
      return this.status === "done" || this.status === "close"
        ? 12
        : this.capacity;
    },
    displayBooked() {
      // close 클래스는 booked를 12로 표시, 나머지는 booked 그대로
      return this.status === "close" ? 12 : this.booked;
    },
    displayDots() {
      // capacityDots만큼의 도트를 생성하고, 그 중 displayBooked만큼만 점등
      const dots = [];
      for (let i = 1; i <= this.capacityDots; i++) {
        dots.push({
          n: i,
          isOn: i <= this.displayBooked,
        });
      }
      return dots;
    },
    computedStatus() {
      if (this.booked >= this.capacity) return "close";
      const remain = this.capacity - this.booked;
      if (remain <= 3) return "warn";
      return "open";
    },
    finalStatus() {
      if (this.status) return this.status;
      if (this.mine) return "done";
      return this.computedStatus;
    },
  },
  methods: {
    getIconPath(type) {
      try {
        return require(`@/assets/icons/${type.toLowerCase()}.png`);
      } catch (e) {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.row {
  position: relative;
  display: grid;
  grid-template-columns: 56px 1fr auto;
  align-items: center;
  gap: 16px;
  background: #0a0a0a;
  border-radius: 10px;
  padding: 18px 16px;
}

.icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 5px;
  border: 1px solid;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-wrap.hyb {
  border-color: #1f472d;
  background: rgba(31, 71, 45, 0.2);
}
.icon-wrap.core {
  border-color: #4e3511;
  background: rgba(78, 53, 17, 0.2);
}
.icon-wrap.car {
  border-color: #4d1c1b;
  background: rgba(77, 28, 27, 0.3);
}

.icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.info {
  display: grid;
  gap: 8px;
}

.top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.type {
  font-size: 18px;
  letter-spacing: -0.02em;
}

.type.hyb {
  color: #49de80;
}
.type.core {
  color: #f59e0c;
}
.type.car {
  color: #ef4444;
}

.bottom {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dots {
  display: flex;
  gap: 4px;
}

.dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #333;
}

.dot.on {
  background: #c8c8c8;
}

.count {
  color: #8e8e8e;
  font-size: 14px;
}

.right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chip {
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border-radius: 5px;
  font-size: 14px;
}

.chip--done {
  border: 1px solid #78ff47;
  color: #78ff47;
  background: #1b1d1e;
}

.chip--close {
  border: 1px solid #333;
  color: #8e8e8e;
  background: #1b1d1e;
  width: 79px;
  min-width: 79px;
  box-sizing: border-box;
}

.chip--warn {
  border: 1px solid #ff817f;
  color: #ff817f;
  background: rgba(77, 28, 27, 0.3);
}

.book-btn {
  width: 79px;
  height: 28px;
  border-radius: 5px;
  font-size: 14px;
  line-height: 1;
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .row {
    gap: 12px;
    grid-template-columns: 48px 1fr auto;
  }

  .icon-wrap {
    width: 48px;
    height: 48px;
  }

  .icon {
    width: 28px;
    height: 28px;
  }

  .bottom {
    gap: 6px;
  }

  .count {
    font-size: 13px;
    white-space: nowrap;
  }

  .right {
    gap: 8px;
  }

  .chip {
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
    white-space: nowrap;
  }

  .chip--done {
    width: 72px;
    min-width: 72px;
    padding: 0 6px;
    font-size: 11px;
    letter-spacing: -0.5px;
    box-sizing: border-box;
  }

  .chip--close {
    width: 72px;
    min-width: 72px;
    padding: 0 6px;
    box-sizing: border-box;
  }

  .book-btn {
    width: 72px !important;
    height: 24px !important;
    font-size: 11px !important;
    padding: 0 6px !important;
    box-sizing: border-box !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
    min-width: 72px !important;
    letter-spacing: -0.5px !important;
  }

  .book-btn span {
    white-space: nowrap !important;
    overflow: visible !important;
    display: inline-block !important;
  }
}

/* 작은 화면 (아이폰 SE 등 375px 이하) */
@media (max-width: 375px) {
  .row {
    gap: 10px;
    padding: 14px 12px;
    grid-template-columns: 40px 1fr auto;
  }

  .icon-wrap {
    width: 40px;
    height: 40px;
  }

  .icon {
    width: 24px;
    height: 24px;
  }

  .time {
    font-size: 16px;
  }

  .type {
    font-size: 16px;
  }

  .bottom {
    gap: 6px;
  }

  .count {
    font-size: 13px;
    white-space: nowrap;
  }

  .right {
    gap: 6px;
  }

  .chip {
    height: 24px;
    padding: 0 6px;
    font-size: 11px;
  }

  .chip--done {
    width: 68px;
    min-width: 68px;
    padding: 0 4px;
    font-size: 10px;
    letter-spacing: -0.5px;
  }

  .chip--close {
    width: 68px;
    min-width: 68px;
    padding: 0 4px;
    box-sizing: border-box;
  }

  .book-btn {
    width: 68px !important;
    padding: 0 4px !important;
    font-size: 10px !important;
    min-width: 68px !important;
    letter-spacing: -0.5px !important;
  }
}
</style>
